import type { MessageKey } from '@ralph/contracts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * What the runs domain's refusals say, in English (the source of truth AND the type).
 *
 * `Extract<MessageKey, 'runs.${string}'>` rather than a hand-written union of thirty key
 * names: it means this file owes a sentence for every `runs.*` key the contract declares, so
 * adding one there and forgetting the words here fails `tsc` on this line instead of in the
 * aggregator. The check is the same one the aggregator would eventually make; putting it here
 * names the file that owes the work.
 */
export const runs: Pick<ServerMessages, Extract<MessageKey, `runs.${string}`>> = {
  // --- a run ---------------------------------------------------------------

  'runs.not_found': () => 'That run no longer exists.',
  'runs.already_finished': () => 'This run has already finished.',
  'runs.not_yours_to_cancel': () => 'Only the person who started this run can cancel it.',
  'runs.checkpoint_unusable': () =>
    'This run cannot be picked up again: what it saved is missing, or was written by an older version.',
  'runs.no_resolved_approval': () => 'This run has no decided approval to carry on from.',
  'runs.delegation_parent_gone': ({ runId }) => `Run ${runId} no longer exists, so nothing can be delegated from it.`,

  // --- an approval ---------------------------------------------------------

  'runs.approval_not_found': () => 'That approval no longer exists.',
  'runs.approval_not_yours': () => 'Only the person who started this run can decide it.',
  'runs.approval_already_resolved': () => 'This approval has already been decided.',
  'runs.approval_expired': () => 'This approval expired. Ask again to get a fresh proposal.',
  'runs.approval_proposal_changed': () =>
    'The proposed action changed since it was shown to you. Review the new proposal before deciding.',
  'runs.edited_arguments_required': () => 'Editing before approval needs the edited arguments.',
  'runs.edited_arguments_invalid': ({ tool }) =>
    `The edited arguments are not what “${tool}” accepts. Check them and try again.`,
  'runs.tool_gone': () => 'That tool is no longer available.',

  // --- a conversation ------------------------------------------------------

  'runs.conversation_gone': () => 'That conversation no longer exists.',
  'runs.conversation_archived': () => 'This conversation is archived.',
  'runs.conversation_not_written': () => 'The conversation could not be created. Try again.',
  'runs.message_pair_not_written': () => 'Your message could not be saved. Try again.',

  // --- the agent behind a conversation -------------------------------------

  // Not available, and suspended: `agents.not_available`, `agents.suspended` and
  // `agents.suspended_with_reason`. Retirement keeps its own pair here because this sentence
  // adds something only a conversation can say — that the transcript is still readable.
  'runs.agent_retired': () => 'This agent has been retired by its owner. You can still read this conversation.',
  'runs.agent_retired_with_replacement': ({ replacement }) =>
    `This agent has been retired by its owner — “${replacement}” replaces it. You can still read this conversation.`,
  'runs.agent_drafted': () =>
    'This agent is not switched on. Its owner has taken it back to a draft, so new messages cannot be sent. ' +
    'You can still read this conversation.'
}
